import { Link } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getWhatsAppLink } from '../config/constants';
import { cn } from '../lib/utils';
import logo from '../assets/logo.png';

declare global {
  interface Window {
    gtag: (
      command: 'event',
      action: string,
      params: {
        event_category: string;
        event_label: string;
        source?: string;
        value?: number;
      }
    ) => void;
  }
}

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleWhatsAppClick = (source: 'desktop' | 'mobile') => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'click_whatsapp', {
        event_category: 'engagement',
        event_label: 'cta_deposer_cv',
        source: source,
        value: 1
      });
    }
  };

  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-40">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                src={logo}
                alt="Emploi Paysagiste Logo"
                className="h-36 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-6">
            <Link
              to="/"
              className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium"
            >
              Accueil
            </Link>
            <Link
              to="/about"
              className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium"
            >
              À propos 
            </Link>
            <Link
              to="/jobs"
              className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium"
            >
              Offres d'emploi
            </Link>
            
            <a
              href={getWhatsAppLink()}
              onClick={() => handleWhatsAppClick('desktop')}
              className="block px-4 py-2 bg-primary-main text-white rounded-md hover:bg-primary-dark text-base font-medium text-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              Déposer mon CV
            </a>

            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                {user?.worker_id ? (
                  <>
                    <Link
                      to="/profile"
                      className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium flex items-center"
                    >
                      <User className="h-5 w-5 mr-2" />
                      Mon Profil
                    </Link>
                    {/* <Link
                      to="/applications"
                      className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium"
                    >
                      Mes candidatures
                    </Link> */}
                  </>
                ) : (
                  <Link
                    to="/dashboard"
                    className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium"
                  >
                    Tableau de bord
                  </Link>
                )}
                <button
                  onClick={logout}
                  className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium flex items-center"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Déconnexion
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium"
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="flex items-center text-text-primary hover:text-primary-main px-3 py-2 rounded-md text-base font-medium"
                >
                  <User className="h-5 w-5 mr-2" />
                  Inscription
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="text-text-primary hover:text-primary-main"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-8 w-8" />
              ) : (
                <Menu className="h-8 w-8" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={cn(
          'sm:hidden',
          isMenuOpen ? 'block' : 'hidden'
        )}
      >
        <div className="px-2 pt-3 pb-4 space-y-3">
          <Link
            to="/"
            className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
          >
            Accueil
          </Link>
          <Link
            to="/about"
            className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
          >
            À propos 
          </Link>
          <Link
            to="/jobs"
            className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
          >
            Offres d'emploi
          </Link>
          
          <a
            href={getWhatsAppLink()}
            onClick={() => handleWhatsAppClick('mobile')}
            className="block px-4 py-2 bg-primary-main text-white rounded-md hover:bg-primary-dark text-base font-medium text-center"
            target="_blank"
            rel="noopener noreferrer"
          >
            Déposer mon CV
          </a>

          {isAuthenticated ? (
            <>
              {user?.worker_id ? (
                <>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
                  >
                    Mon Profil
                  </Link>
                  {/* <Link
                    to="/applications"
                    className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
                  >
                    Mes candidatures
                  </Link> */}
                </>
              ) : (
                <Link
                  to="/dashboard"
                  className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
                >
                  Tableau de bord
                </Link>
              )}
              <button
                onClick={logout}
                className="block w-full text-left px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
              >
                Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
              >
                Connexion
              </Link>
              <Link
                to="/register"
                className="block px-4 py-2 rounded-md text-base font-medium text-text-primary hover:text-primary-main"
              >
                Inscription
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}